import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Inbox, Users, Mail, Stethoscope, FileText, Building2, Loader2 } from 'lucide-react';

const OPEN_SEARCH_EVENT = 'global-search:open';

/**
 * Programmatically open the global search dialog.
 * Used by the sidebar search button.
 */
export function openGlobalSearch(): void {
  document.dispatchEvent(new CustomEvent(OPEN_SEARCH_EVENT));
}
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { useDebounce } from '@/hooks/use-debounce';
import { getSearchClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';
import type { SearchResultItem, SearchResults } from '@vagusx/clients';

const resultGroups: {
  key: keyof SearchResults;
  heading: string;
  icon: React.ComponentType<{ className?: string }>;
}[] = [
  { key: 'tasks', heading: 'Tasks', icon: Inbox },
  { key: 'patients', heading: 'Patients', icon: Users },
  { key: 'emails', heading: 'Emails', icon: Mail },
  { key: 'providers', heading: 'Providers', icon: Stethoscope },
  { key: 'documents', heading: 'Documents', icon: FileText },
  { key: 'entities', heading: 'Entities', icon: Building2 },
];

function SearchResult({
  item,
  icon: Icon,
  onSelect,
}: {
  item: SearchResultItem;
  icon: React.ComponentType<{ className?: string }>;
  onSelect: (item: SearchResultItem) => void;
}): React.ReactElement {
  return (
    <CommandItem onSelect={() => onSelect(item)} className="flex items-start gap-2 py-2.5">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium">{item.title}</span>
          {item.status && (
            <Badge variant="outline" className="shrink-0 text-[10px] px-1.5 py-0">
              {item.status}
            </Badge>
          )}
          {item.priority && item.priority !== 'medium' && (
            <Badge variant="secondary" className="shrink-0 text-[10px] px-1.5 py-0">
              {item.priority}
            </Badge>
          )}
        </div>
        {item.subtitle && (
          <div className="truncate text-xs text-muted-foreground mt-0.5">{item.subtitle}</div>
        )}
      </div>
    </CommandItem>
  );
}

export function GlobalSearchDialog(): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 300);
  const navigate = useNavigate();

  // Cmd+K / Ctrl+K keyboard shortcut
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent): void {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    }
    function handleOpenEvent(): void {
      setOpen(true);
    }
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener(OPEN_SEARCH_EVENT, handleOpenEvent);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener(OPEN_SEARCH_EVENT, handleOpenEvent);
    };
  }, []);

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.search.query(debouncedQuery),
    queryFn: ({ signal }) => getSearchClient().search(debouncedQuery, signal),
    enabled: debouncedQuery.length >= 2,
    staleTime: 30_000,
  });

  const handleSelect = useCallback(
    (result: SearchResultItem) => {
      setOpen(false);
      setQuery('');
      switch (result.type) {
        case 'task':
          navigate({ to: '/tasks/$taskId', params: { taskId: result.id } });
          break;
        case 'patient':
          navigate({ to: '/patients/$id', params: { id: result.id } });
          break;
        case 'email':
          navigate({ to: '/tasks/$taskId', params: { taskId: result.id } });
          break;
        case 'provider':
          navigate({ to: '/setup/providers' });
          break;
        case 'document':
          navigate({ to: '/documents/$id', params: { id: result.id } });
          break;
        case 'entity':
          navigate({ to: '/setup/entities', search: { id: result.id } });
          break;
      }
    },
    [navigate]
  );

  const hasResults = data && resultGroups.some((g) => data[g.key].length > 0);

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setQuery('');
      }}
    >
      <DialogHeader className="sr-only">
        <DialogTitle>Search</DialogTitle>
        <DialogDescription>
          Search across tasks, patients, emails, providers, documents, and entities
        </DialogDescription>
      </DialogHeader>
      <DialogContent className="overflow-hidden p-0" showCloseButton={false}>
        <Command
          shouldFilter={false}
          className="**:data-[slot=command-input-wrapper]:h-12 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]]:px-2 [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12"
        >
          <CommandInput
            placeholder="Search tasks, patients, emails, providers, documents, entities..."
            value={query}
            onValueChange={setQuery}
          />
          <CommandList className="max-h-[400px]">
            {debouncedQuery.length < 2 && (
              <div className="py-6 text-center text-sm text-muted-foreground">
                Type at least 2 characters to search
              </div>
            )}

            {isLoading && debouncedQuery.length >= 2 && (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                <span className="ml-2 text-sm text-muted-foreground">Searching...</span>
              </div>
            )}

            {!isLoading && debouncedQuery.length >= 2 && !hasResults && (
              <CommandEmpty>No results found</CommandEmpty>
            )}

            {resultGroups.map(({ key, heading, icon }) => {
              const items = data?.[key];
              if (!items || items.length === 0) return null;
              return (
                <CommandGroup key={key} heading={heading}>
                  {items.map((item) => (
                    <SearchResult
                      key={`${key}-${item.id}`}
                      item={item}
                      icon={icon}
                      onSelect={handleSelect}
                    />
                  ))}
                </CommandGroup>
              );
            })}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
